<template>
  <a-table
    class="table datatable thead-light dq-report-table"
    :columns="columns"
    :data-source="dataSource"
    :loading="loading"
    :pagination="pagination"
    row-key="key"
    @change="onChange"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'Name'">
        <div class="d-flex align-items-center">
          <a href="javascript:void(0);">
            <DqAvatar
              :src="record.Image"
              :name="record.name"
              size-class="avatar-md"
              shape-class="rounded-circle"
            />
          </a>
          <div class="ms-2">
            <p class="text-dark mb-0">{{ record.name }}</p>
            <span class="fs-12">{{ record.role }}</span>
          </div>
        </div>
      </template>

      <template v-else-if="column.key === 'EmployeeID'">
        <span class="badge bg-light text-dark">{{ record.emp_code || "-" }}</span>
      </template>

      <template v-else-if="column.key === 'Gross'">
        {{ formatAmount(record.gross) }}
      </template>

      <template v-else-if="column.key === 'Chargeable'">
        {{ formatAmount(record.chargeable) }}
      </template>

      <template v-else-if="column.key === 'PAYE'">
        {{ formatAmount(record.paye) }}
      </template>

      <template v-else-if="column.key === 'Actions'">
        <div class="d-flex gap-2">
          <button
            class="btn btn-outline-primary btn-sm d-inline-flex align-items-center"
            :disabled="loading || isBusy(record.id)"
            @click="!isBusy(record.id) && $emit('download', record)"
          >
            <span
              v-if="isDownloading(record.id)"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            <i v-else class="ti ti-download"></i>
            <span class="ms-1 d-none d-md-inline">Download</span>
          </button>

          <button
            class="btn btn-outline-secondary btn-sm d-inline-flex align-items-center"
            :disabled="loading || isBusy(record.id)"
            @click="!isBusy(record.id) && $emit('email', record)"
          >
            <span
              v-if="isEmailing(record.id)"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            <i v-else class="ti ti-mail"></i>
            <span class="ms-1 d-none d-md-inline">Email</span>
          </button>
        </div>
      </template>
    </template>
  </a-table>
</template>

<script setup>
import { computed } from "vue";
import DqAvatar from "@/components/shared/DqAvatar.vue";

const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  total: { type: Number, default: 0 },
  pageSize: { type: Number, default: 25 },
  current: { type: Number, default: 1 },
  downloadingIds: { type: [Array, Set, Object], default: () => [] },
  emailingIds: { type: [Array, Set, Object], default: () => [] },
});

const emit = defineEmits(["change", "download", "email"]);

const columns = [
  { sorter: false },
  {
    title: "Name",
    dataIndex: "name",
    key: "Name",
    sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
  },
  {
    title: "Emp ID",
    dataIndex: "emp_code",
    key: "EmployeeID",
    width: 120,
  },
  {
    title: "Months",
    dataIndex: "months_with_data",
    key: "Months",
    width: 110,
  },
  {
    title: "Latest Month",
    dataIndex: "latest_month",
    key: "LatestMonth",
    width: 130,
  },
  {
    title: "Latest Gross",
    dataIndex: "latest_gross",
    key: "LatestGross",
    width: 140,
  },
  {
    title: "Latest Chargeable",
    dataIndex: "latest_chargeable",
    key: "LatestChargeable",
    width: 140,
  },
  {
    title: "Latest PAYE",
    dataIndex: "latest_paye",
    key: "LatestPAYE",
    width: 130,
  },
  {
    title: "Actions",
    key: "Actions",
    width: 140,
  },
];

const dataSource = computed(() =>
  (props.rows || []).map((e, idx) => ({
    key: e.id || idx,
    id: e.id,
    Image: e.avatar_url || "",
    name: `${e.first_name || ""} ${e.last_name || ""}`.trim(),
    role: e.designation?.name || "-",
    emp_code: e.employee_id || "-",
    months_with_data: e.legacy_p9_months_with_data || 0,
    latest_month: e.legacy_p9_latest_month || "-",
    latest_gross: e.legacy_p9_latest_snapshot?.gross || 0,
    latest_chargeable: e.legacy_p9_latest_snapshot?.chargeable || 0,
    latest_paye: e.legacy_p9_latest_snapshot?.paye || 0,
  }))
);

const pagination = computed(() => ({
  total: props.total || (props.rows?.length ?? 0),
  pageSize: props.pageSize,
  current: props.current,
  showTotal: (t) => `Total ${t} employees`,
}));

function onChange(pagination, filters, sorter) {
  emit("change", pagination, filters, sorter);
}

function has(setLike, id) {
  if (!setLike) return false;
  if (Array.isArray(setLike)) return setLike.includes(id);
  if (setLike instanceof Set) return setLike.has(id);
  return !!setLike[id];
}

function isDownloading(id) {
  return has(props.downloadingIds, id);
}

function isEmailing(id) {
  return has(props.emailingIds, id);
}

function isBusy(id) {
  return isDownloading(id) || isEmailing(id);
}

function formatAmount(value) {
  const amount = Number(value || 0);
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
</script>

<style scoped>
@media (max-width: 767.98px) {
  :deep(.dq-report-table .ant-table) {
    min-width: 820px;
  }
}
</style>
